import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle, Award, Calendar, ExternalLink, ShieldCheck, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { API } from '@/utils/api';
import { getCertConfig } from '@/utils/certificateGenerator';
import { getBadgeInfo } from '@/data/badgeCatalog';
import { Button } from '@/components/ui/button';

const VerifyCertificate = () => {
  const { certId } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verify = async () => {
      try {
        const res = await axios.get(`${API}/certificates/${certId}`);
        setResult(res.data);
      } catch (e) {
        setResult({ valid: false, reason: 'Verification service unavailable' });
      }
      setLoading(false);
    };
    verify();
  }, [certId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 border-4 border-[#FFB800]/30 border-t-[#FFB800] rounded-full animate-spin mx-auto" />
          <div className="text-white font-heading text-lg">Verifying certificate...</div>
          <div className="text-[#71717A] text-sm font-code">{certId}</div>
        </div>
      </div>
    );
  }

  const isValid = result?.valid;
  const config = isValid ? getCertConfig(result.badge_id) : null;
  const badgeInfo = isValid ? getBadgeInfo(result.badge_id) : null;
  const [aR, aG, aB] = config?.accentColor || [255, 184, 0];
  const [a2R, a2G, a2B] = config?.accentColor2 || [255, 136, 0];
  const rgb = `rgb(${aR}, ${aG}, ${aB})`;
  const rgb2 = `rgb(${a2R}, ${a2G}, ${a2B})`;

  return (
    <div 
      data-testid="verify-certificate-container"
      className="min-h-screen bg-[#050505] flex items-center justify-center p-6 relative overflow-hidden"
    >
      {/* Ambient glow */}
      {isValid && (
        <>
          <motion.div
            className="absolute top-20 right-20 w-96 h-96 rounded-full blur-3xl opacity-30"
            style={{ background: rgb }}
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 6, repeat: Infinity }}
          />
          <motion.div
            className="absolute bottom-20 left-20 w-96 h-96 rounded-full blur-3xl opacity-30"
            style={{ background: rgb2 }}
            animate={{ scale: [1, 1.3, 1] }}
            transition={{ duration: 6, repeat: Infinity, delay: 2 }}
          />
        </>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 max-w-2xl w-full"
      >
        {/* Header status */}
        <div className={`rounded-2xl p-1 mb-6 ${isValid ? '' : ''}`}
          style={isValid ? {
            background: `linear-gradient(135deg, ${rgb}, ${rgb2})`
          } : { background: 'linear-gradient(135deg, #DC2626, #991B1B)' }}
        >
          <div className="bg-[#0F0F12] rounded-xl p-8 text-center">
            {isValid ? (
              <>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                  className="w-24 h-24 rounded-full mx-auto flex items-center justify-center mb-4"
                  style={{
                    background: `linear-gradient(135deg, ${rgb}, ${rgb2})`,
                    boxShadow: `0 0 40px ${rgb}66`
                  }}
                >
                  <ShieldCheck className="w-12 h-12 text-white" />
                </motion.div>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                  <span className="text-green-400 font-code text-xs font-bold uppercase tracking-widest">
                    Certificate Verified
                  </span>
                </div>
                <h1 className="font-heading text-3xl font-bold text-white">
                  Authentic & Valid
                </h1>
              </>
            ) : (
              <>
                <div className="w-24 h-24 rounded-full mx-auto flex items-center justify-center mb-4 bg-red-500/20 border-2 border-red-500">
                  <XCircle className="w-12 h-12 text-red-400" />
                </div>
                <div className="text-red-400 font-code text-xs font-bold uppercase tracking-widest mb-2">
                  Verification Failed
                </div>
                <h1 className="font-heading text-3xl font-bold text-white mb-2">
                  Invalid Certificate
                </h1>
                <p className="text-[#A1A1AA] font-body text-sm">
                  {result?.reason || 'This certificate could not be verified'}
                </p>
              </>
            )}
          </div>
        </div>

        {/* Details */}
        {isValid && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-[#0F0F12] border border-white/10 rounded-2xl p-8 space-y-6"
          >
            {/* Learner Name (big) */}
            <div className="text-center">
              <div className="text-[#71717A] text-xs font-code uppercase tracking-widest mb-2">
                Awarded To
              </div>
              <div className="font-heading text-4xl font-bold text-white">
                {result.learner_name}
              </div>
              <div className="h-px w-24 mx-auto my-4" style={{ background: rgb }} />
            </div>

            {/* Achievement */}
            <div className="text-center space-y-3">
              <div className="text-[#71717A] text-xs font-code uppercase tracking-widest">
                For Achievement Of
              </div>
              <div className="text-4xl">{badgeInfo?.icon}</div>
              <div className="font-heading text-2xl font-bold" style={{ color: rgb }}>
                {config.subtitle}
              </div>
              <p className="text-[#A1A1AA] text-sm font-body max-w-md mx-auto leading-relaxed">
                {config.achievement}
              </p>
            </div>

            <div className="h-px bg-white/10" />

            {/* Meta */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-[#1A1A20] border border-white/10 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-4 h-4" style={{ color: rgb }} />
                  <span className="text-[#71717A] text-xs font-code uppercase tracking-widest">Issued On</span>
                </div>
                <div className="text-white font-body">
                  {new Date(result.issued_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </div>
              </div>
              <div className="bg-[#1A1A20] border border-white/10 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Award className="w-4 h-4" style={{ color: rgb }} />
                  <span className="text-[#71717A] text-xs font-code uppercase tracking-widest">Certificate ID</span>
                </div>
                <div className="text-white font-code text-sm break-all">
                  {result.cert_id}
                </div>
              </div>
            </div>

            {/* Issuer */}
            <div className="bg-gradient-to-r rounded-lg p-4 border" 
              style={{ 
                background: `linear-gradient(to right, ${rgb}15, ${rgb2}15)`,
                borderColor: rgb + '40'
              }}
            >
              <div className="text-[#71717A] text-xs font-code uppercase tracking-widest mb-1">
                Issued By
              </div>
              <div className="text-white font-heading font-semibold">
                DSA With Kapil · Visual Learning Lab
              </div>
              <a
                href="https://javadsa.sarlayash.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-code flex items-center gap-1 mt-1 hover:underline"
                style={{ color: rgb }}
              >
                javadsa.sarlayash.com <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </motion.div>
        )}

        {/* CTA */}
        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            data-testid="verify-back-home"
            onClick={() => navigate('/')}
            className="bg-[#FFB800] hover:bg-[#FFC520] text-black font-heading font-semibold"
          >
            Explore DSA With Kapil
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-[#71717A] text-xs font-code">
          Publicly verifiable certificate · No login required
        </div>
      </motion.div>
    </div>
  );
};

export default VerifyCertificate;
